import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Target, 
  Building2, 
  Phone, 
  Mail, 
  Plus, 
  Search,
  ArrowRight,
  DollarSign,
  Clock,
  UserCheck,
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leads = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const statusColors = {
    new: 'bg-blue-50 text-blue-700 border-blue-200',
    contacted: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    qualified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unqualified: 'bg-gray-50 text-gray-700 border-gray-200',
    converted: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  const statusLabels = {
    new: isRTL ? 'جديد' : 'New',
    contacted: isRTL ? 'تم التواصل' : 'Contacted',
    qualified: isRTL ? 'مؤهل' : 'Qualified',
    unqualified: isRTL ? 'غير مؤهل' : 'Unqualified',
    converted: isRTL ? 'تم التحويل' : 'Converted'
  };

  const sourceLabels = {
    website: isRTL ? 'الموقع' : 'Website',
    referral: isRTL ? 'إحالة' : 'Referral',
    cold_call: isRTL ? 'اتصال بارد' : 'Cold Call',
    linkedin: 'LinkedIn',
    event: isRTL ? 'حدث' : 'Event',
    advertisement: isRTL ? 'إعلان' : 'Advertisement',
    other: isRTL ? 'أخرى' : 'Other'
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/leads`);
      setLeads(res.data);
    } catch (err) {
      console.error(err);
      toast.error(isRTL ? 'فشل تحميل العملاء المحتملين' : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingLead) {
        await axios.put(`${API_URL}/api/crm/leads/${editingLead.id}`, formData);
        toast.success(isRTL ? 'تم تحديث العميل المحتمل' : 'Lead updated');
      } else {
        await axios.post(`${API_URL}/api/crm/leads`, formData);
        toast.success(isRTL ? 'تم إضافة العميل المحتمل' : 'Lead added');
      }
      setShowDialog(false);
      setEditingLead(null);
      setFormData({});
      fetchLeads();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ العميل المحتمل' : 'Failed to save lead');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'هل أنت متأكد من حذف هذا العميل المحتمل؟' : 'Are you sure you want to delete this lead?')) {
      try {
        await axios.delete(`${API_URL}/api/crm/leads/${id}`);
        toast.success(isRTL ? 'تم حذف العميل المحتمل' : 'Lead deleted');
        fetchLeads();
      } catch (err) {
        toast.error(isRTL ? 'فشل حذف العميل المحتمل' : 'Failed to delete lead');
      }
    }
  };

  const handleConvert = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/crm/leads/${id}/convert`);
      toast.success(isRTL ? 'تم تحويل العميل المحتمل إلى عميل بنجاح!' : 'Lead converted to client successfully!');
      fetchLeads();
    } catch (err) {
      toast.error(isRTL ? 'فشل تحويل العميل المحتمل' : 'Failed to convert lead');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/crm/leads/${id}`, { status: newStatus });
      toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
      fetchLeads();
    } catch (err) {
      toast.error(isRTL ? 'فشل تحديث الحالة' : 'Failed to update status');
    }
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setFormData(lead);
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingLead(null);
    setFormData({ source: 'website' });
    setShowDialog(true);
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    converted: leads.filter(l => l.status === 'converted').length
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('pages.leads.title')}</h1>
          <p className="text-muted-foreground">{t('pages.leads.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Plus className="h-4 w-4" /> {t('pages.leads.addLead')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-sm text-muted-foreground">{isRTL ? 'إجمالي العملاء المحتملين' : 'Total Leads'}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
          <div className="text-sm text-muted-foreground">{statusLabels.new}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-2xl font-bold text-emerald-600">{stats.qualified}</div>
          <div className="text-sm text-muted-foreground">{statusLabels.qualified}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{stats.converted}</div>
          <div className="text-sm text-muted-foreground">{statusLabels.converted}</div>
        </div>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4`} />
          <Input 
            placeholder={t('pages.leads.searchPlaceholder')} 
            className={isRTL ? 'pr-10 text-right' : 'pl-10'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder={isRTL ? 'تصفية الحالة' : 'Filter status'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</SelectItem>
            <SelectItem value="new">{statusLabels.new}</SelectItem>
            <SelectItem value="contacted">{statusLabels.contacted}</SelectItem>
            <SelectItem value="qualified">{statusLabels.qualified}</SelectItem>
            <SelectItem value="unqualified">{statusLabels.unqualified}</SelectItem>
            <SelectItem value="converted">{statusLabels.converted}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الشركة' : 'Company'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'جهة الاتصال' : 'Contact'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.leads.source')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'القيمة المتوقعة' : 'Est. Value'}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.leads.status')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'تاريخ الإنشاء' : 'Created'}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <div className="font-medium">{lead.company_name}</div>
                      <div className="text-xs text-muted-foreground">{lead.industry || (isRTL ? 'لا يوجد قطاع' : 'No industry')}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className={isRTL ? 'text-right' : ''}>
                  <div className="text-sm">{lead.contact_name || '-'}</div>
                  <div className="text-xs text-muted-foreground">{lead.email}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{sourceLabels[lead.source] || lead.source}</Badge>
                </TableCell>
                <TableCell>
                  {lead.estimated_value ? (
                    <span className="font-mono">{currencySymbol} {lead.estimated_value.toLocaleString()}</span>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Badge className={statusColors[lead.status]}>{statusLabels[lead.status]}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {format(new Date(lead.created_at), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(lead)}>
                        <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => updateStatus(lead.id, 'contacted')}>
                        <Phone className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'تحديد كـ تم التواصل' : 'Mark Contacted'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(lead.id, 'qualified')}>
                        <UserCheck className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'تحديد كـ مؤهل' : 'Mark Qualified'}
                      </DropdownMenuItem>
                      {lead.status === 'qualified' && (
                        <DropdownMenuItem onClick={() => handleConvert(lead.id)} className="text-purple-600">
                          <CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.leads.convertToDeal')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(lead.id)}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredLeads.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <Target className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{isRTL ? 'لا يوجد عملاء محتملين' : 'No leads found'}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-md ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader>
            <DialogTitle>{editingLead ? (isRTL ? 'تعديل العميل المحتمل' : 'Edit Lead') : t('pages.leads.addLead')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم الشركة *' : 'Company Name *'}</Label>
              <Input 
                value={formData.company_name || ''}
                onChange={e => setFormData({...formData, company_name: e.target.value})} 
                placeholder={isRTL ? 'شركة أكمي' : 'Acme Corp'}
                className={isRTL ? 'text-right' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'جهة الاتصال' : 'Contact Person'}</Label>
              <Input 
                value={formData.contact_name || ''}
                onChange={e => setFormData({...formData, contact_name: e.target.value})} 
                placeholder={isRTL ? 'أحمد محمد' : 'John Smith'}
                className={isRTL ? 'text-right' : ''}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.clients.email')}</Label>
                <Input 
                  type="email"
                  value={formData.email || ''}
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.clients.phone')}</Label>
                <Input 
                  value={formData.phone || ''}
                  onChange={e => setFormData({...formData, phone: e.target.value})} 
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.leads.source')}</Label>
                <Select value={formData.source || 'website'} onValueChange={v => setFormData({...formData, source: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">{sourceLabels.website}</SelectItem>
                    <SelectItem value="referral">{sourceLabels.referral}</SelectItem>
                    <SelectItem value="cold_call">{sourceLabels.cold_call}</SelectItem>
                    <SelectItem value="linkedin">{sourceLabels.linkedin}</SelectItem>
                    <SelectItem value="event">{sourceLabels.event}</SelectItem>
                    <SelectItem value="advertisement">{sourceLabels.advertisement}</SelectItem>
                    <SelectItem value="other">{sourceLabels.other}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pages.clients.industry')}</Label>
                <Input 
                  value={formData.industry || ''}
                  onChange={e => setFormData({...formData, industry: e.target.value})} 
                  placeholder={isRTL ? 'التقنية' : 'Technology'}
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'القيمة المتوقعة ($)' : 'Estimated Value ($)'}</Label>
              <Input 
                type="number"
                value={formData.estimated_value || ''}
                onChange={e => setFormData({...formData, estimated_value: parseFloat(e.target.value) || 0})} 
                placeholder="10000"
                className={isRTL ? 'text-right' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea 
                value={formData.notes || ''}
                onChange={e => setFormData({...formData, notes: e.target.value})} 
                placeholder={isRTL ? 'ملاحظات إضافية...' : 'Additional notes...'}
                rows={3}
                className={isRTL ? 'text-right' : ''}
              />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingLead ? (isRTL ? 'تحديث' : 'Update') : t('pages.common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
